import { RefObject, useLayoutEffect, useEffect, useState } from "react"

interface IUseDescriptionFitOptions {
  scale?: number // множник базового розміру шрифту, default: 1
  observe?: boolean // true = ResizeObserver, false = одноразовий useLayoutEffect
  enabled?: boolean // false = пропустити весь DOM-доступ
}

interface IUseDescriptionFitResult {
  fontSize: number | undefined // px-значення, undefined коли disabled
  lineClamp: number | undefined // ціле число ліній, undefined коли disabled
}

const BASE_RATIO = 0.09 // fontSize = containerWidth * 0.1 * scale
const LINE_HEIGHT = 1.1 // відповідає SCSS line-height: 110%

export const useDescriptionFit = (
  ref: RefObject<HTMLDivElement | null>,
  { scale = 1, observe = true, enabled = true }: IUseDescriptionFitOptions = {}
): IUseDescriptionFitResult => {
  const [result, setResult] = useState<IUseDescriptionFitResult>({
    fontSize: undefined,
    lineClamp: undefined
  })

  const calculate = () => {
    const el = ref.current
    if (!el) return

    const width = el.offsetWidth
    const height = el.offsetHeight

    const fontSize = width * BASE_RATIO * scale
    const lineClamp = Math.floor(height / (fontSize * LINE_HEIGHT))

    setResult({ fontSize, lineClamp })
  }

  // ResizeObserver-стратегія
  useEffect(() => {
    if (!enabled || !observe) return

    const el = ref.current
    if (!el) return

    const observer = new ResizeObserver(() => calculate())
    observer.observe(el)
    calculate() // перший запуск одразу

    return () => observer.disconnect()
  }, [enabled, observe, scale])

  // Одноразова стратегія (useLayoutEffect)
  useLayoutEffect(() => {
    if (!enabled || observe) return
    calculate()
  }, [enabled, observe, scale])

  if (!enabled) return { fontSize: undefined, lineClamp: undefined }

  return result
}
